/**
 * Filtry podle štítků nad tabulkami placementů (first-look / refresh, debug, PX, guard…).
 *
 * Každá skupina je přepínač „vše / jedna z voleb"; skupiny se kombinují (AND). U volby
 * je počet řádků, které by po jejím zapnutí zůstaly (s ohledem na ostatní aktivní
 * skupiny), takže je vidět, co filtr udělá, než na něj klikneš. Stav žije v URL
 * (`?f_phase=refresh`) — pohled přežije refresh a jde poslat odkazem.
 *
 * Filtruje se v prohlížeči nad už načtenými řádky; agregace nad tabulkou (karty
 * metrik) dál odpovídají horní filtrační liště, ne těmhle štítkům.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatInt } from '@/lib/formatCz';

export interface FilterOption<T> {
  value: string;
  label: string;
  title?: string;
  test: (row: T) => boolean;
}

export interface FilterGroup<T> {
  key: string;
  label: string;
  options: FilterOption<T>[];
}

const PARAM = (key: string) => `f_${key}`;

/** Řádky po filtrech + lišta s přepínači. `groups` drž stabilní (useMemo). */
export function useBadgeFilters<T>(rows: readonly T[], groups: FilterGroup<T>[]) {
  const [params, setParams] = useSearchParams();

  const active = useMemo(() => {
    const out: Record<string, FilterOption<T>> = {};
    for (const g of groups) {
      const value = params.get(PARAM(g.key));
      const opt = g.options.find((o) => o.value === value);
      if (opt) out[g.key] = opt;
    }
    return out;
  }, [params, groups]);

  const passes = useCallback(
    (row: T, skip?: string) =>
      Object.entries(active).every(([key, opt]) => key === skip || opt.test(row)),
    [active],
  );

  const filtered = useMemo(() => rows.filter((r) => passes(r)), [rows, passes]);

  const setFilter = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(PARAM(key), value);
        else next.delete(PARAM(key));
        return next;
      },
      { replace: true },
    );

  const clearAll = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const g of groups) next.delete(PARAM(g.key));
        return next;
      },
      { replace: true },
    );

  const activeCount = Object.keys(active).length;

  const bar = (
    <div className="badge-filters" role="group" aria-label="Filtry podle štítků">
      {groups.map((g) => {
        // počty: řádky, které projdou ostatními aktivními skupinami + téhle volbě
        const base = rows.filter((r) => passes(r, g.key));
        const current = active[g.key]?.value ?? '';
        return (
          <div key={g.key} className="badge-filters__group">
            <span className="badge-filters__label">{g.label}</span>
            <div className="segmented">
              <button
                type="button"
                className={current === '' ? 'is-active' : undefined}
                aria-pressed={current === ''}
                onClick={() => setFilter(g.key, null)}
              >
                vše
              </button>
              {g.options.map((o) => {
                const n = base.filter(o.test).length;
                return (
                  <button
                    key={o.value}
                    type="button"
                    title={o.title}
                    className={current === o.value ? 'is-active' : undefined}
                    aria-pressed={current === o.value}
                    disabled={n === 0 && current !== o.value}
                    onClick={() => setFilter(g.key, current === o.value ? null : o.value)}
                  >
                    {o.label} <span className="badge-filters__count">{formatInt(n)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      <span className="badge-filters__summary">
        {activeCount > 0 ? (
          <>
            Zobrazeno {formatInt(filtered.length)} z {formatInt(rows.length)}
            <button type="button" className="linklike" onClick={clearAll}>
              zrušit filtry
            </button>
          </>
        ) : (
          <>{formatInt(rows.length)} pozic</>
        )}
      </span>
    </div>
  );

  return { filtered, bar, activeCount };
}
